/**
 * The POS button. Copper for anything that acts, moss for anything that identifies.
 *
 * Two details that matter more here than in a normal app: the press feedback is a transform that
 * fires on `:active` with no JavaScript, so it lands before any network call; and the minimum
 * height is 48px because the counter is a hurried, one-handed environment.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from './cn.js';

export type ButtonVariant = 'action' | 'brand' | 'outline' | 'quiet' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks repeat taps. The label stays, so the button does not resize. */
  loading?: boolean;
  fullWidth?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
};

const VARIANTS: Record<ButtonVariant, string> = {
  action: 'bg-action text-action-on hover:bg-action-hover active:bg-action-active shadow-sm',
  brand: 'bg-brand text-brand-on hover:bg-brand-hover active:bg-brand-active shadow-sm',
  // Alternative paths: the same accent as the primary action, as a border rather than a fill,
  // so it reads as "also possible" next to "do this". Part payment, wholesale, a misc. line.
  outline: 'border border-action bg-transparent text-action-subtle-text hover:bg-action-subtle active:bg-action-subtle',
  quiet: 'bg-surface-sunken text-content-primary hover:bg-action-subtle active:bg-action-subtle',
  ghost: 'bg-transparent text-content-secondary hover:bg-surface-sunken active:bg-surface-sunken',
  danger: 'bg-danger text-danger-on hover:bg-danger-solid-hover shadow-sm',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-compact px-4 text-sm rounded-md gap-2',
  md: 'min-h-touch px-5 text-base rounded-control gap-2.5',
  lg: 'min-h-comfortable px-6 text-lg rounded-control gap-3',
};

export function Button({
  variant = 'action',
  size = 'md',
  loading = false,
  fullWidth = false,
  leading,
  trailing,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex select-none items-center justify-center font-semibold',
        'transition-[background-color,transform,box-shadow] duration-fast ease-standard',
        'active:scale-press disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {loading ? <Spinner /> : leading}
      <span className={cn(loading && 'opacity-70')}>{children}</span>
      {trailing}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
